import { CustomVideoPlayer, Typography } from '@room-match/shared-ui-components';
import { VideoItemCont } from 'pages/Media/Items/Item.styles';
import React, { useMemo } from 'react';
import RefFiles from 'shared/components/RefFiles/RefFiles';
import { CustomTypography } from 'shared/elements/CustomTypography';
import { ICastingRoleAttributes } from 'shared/interfaces/ICastingRole';
import { ellipseText, secondsToTime } from 'shared/utils/room-shared';
import { DirectionText, MediaList, Mediacont, PlainHeading } from './Response.styles';

interface Props {
  data: ICastingRoleAttributes | undefined;
}

interface RequiremenKeyProps {
  new_video: string;
  new_audio: string;
  new_file: string;
  new_other_images: string;
  slate: string;
  full_body: string;
}

// This is the keys we get from the new media request
const requirements_key: RequiremenKeyProps = {
  new_video: 'new_video',
  new_audio: 'new_audio',
  new_file: 'new_files',
  new_other_images: 'new_other_images',
  slate: 'slate',
  full_body: 'full_body',
};

const STLayout: React.FC<Props> = ({ data }) => {
  // Mapping the Key of the New requirements
  const new_requirements_key = useMemo(() => {
    if (!data || !data?.self_tapes_requirements || !data?.self_tapes_requirements?.new_media_requests?.length) {
      return null;
    }
    // This is the array of media request, the data we get is like this (new_file1, new_file2, new_video1, etc...)
    const new_requests = data?.self_tapes_requirements?.new_media_requests;
    // Here we get the key itself and replacing all numeric values in that key, so the output is new_file1 to new_file
    const new_requirements = new_requests?.map((req) => Object.keys(req)[0]?.replace(/\d/g, ''));
    // We use set so we can remove the duplicates
    return new Set(new_requirements);
  }, [data]);

  // A function to check if that key is present in the new_requirements key
  const hasKey = (key: string) => {
    return new_requirements_key?.has(key);
  };

  return (
    <div>
      <div style={{ marginBottom: 30 }}>
        <PlainHeading style={{ color: '#f7bc02', fontWeight: 500, marginBottom: 10 }}>General Notes:</PlainHeading>
        <DirectionText>{data?.project.self_tapes_note || 'No Self tapes note provided'}</DirectionText>
      </div>
      <div>
        <PlainHeading style={{ color: '#f7bc02', fontWeight: 500 }}>Direction:</PlainHeading>
        <DirectionText>
          {data?.self_tapes_requirements?.selections || data?.talent_description || 'No Directions Provided'}
        </DirectionText>
      </div>
      {data && data.self_tapes_directions && (
        <div style={{ display: 'flex', flexWrap: 'wrap', margin: '10px 0' }}>
          {data?.self_tapes_directions.map((i) => (
            <div>
              <VideoItemCont style={{ maxHeight: 114, maxWidth: 233, minHeight: 114, minWidth: 233, marginLeft: 0 }}>
                {/* <VideoItem url={i.media?.attachment || ''} /> */}
                <CustomVideoPlayer
                  classes={{ root: 'profile-video-item', videoPlayer: '', icon: '' }}
                  url={i.media?.attachment || ''}
                  isSmall
                />
              </VideoItemCont>
              <Typography fontSize={14}>{ellipseText(i.description, 16)}</Typography>
              <Typography color="mainGrey" fontSize={14}>
                {typeof i.media?.duration === 'number' ? secondsToTime(Number(i.media?.duration || 0)) : 0}
              </Typography>
            </div>
          ))}
        </div>
      )}
      <Mediacont>
        {data && data?.self_tapes_requirements && (
          <div>
            <PlainHeading style={{ color: '#f7bc02', fontWeight: 500 }}>Media Required:</PlainHeading>

            <MediaList>
              {!!data.self_tapes_requirements.audition_selfie && (
                <CustomTypography>1x Image (Audition Selfie)</CustomTypography>
              )}
              {!!data.self_tapes_requirements.full_body && (
                <CustomTypography color={hasKey(requirements_key.full_body) ? 'warning' : undefined}>
                  1x Image (Full Body)
                </CustomTypography>
              )}
              {data.self_tapes_requirements.other_images?.length > 0 && (
                <CustomTypography color={hasKey(requirements_key.new_other_images) ? 'warning' : undefined}>{`${
                  data.self_tapes_requirements.other_images?.length
                }${
                  data.self_tapes_requirements.other_images?.length === 1
                    ? 'x Image (Other Image)'
                    : 'x Images (Other Images)'
                }`}</CustomTypography>
              )}
              {data.self_tapes_requirements.audio?.length > 0 && (
                <CustomTypography color={hasKey(requirements_key.new_audio) ? 'warning' : undefined}>{`${
                  data.self_tapes_requirements.audio?.length
                }${data.self_tapes_requirements.audio?.length === 1 ? 'x Audio' : 'x Audios'}`}</CustomTypography>
              )}
              {!!data.self_tapes_requirements.slate && (
                <CustomTypography color={hasKey(requirements_key.slate) ? 'warning' : undefined}>
                  1x Slate
                </CustomTypography>
              )}
              {data.self_tapes_requirements.video?.length > 0 && (
                <CustomTypography color={hasKey(requirements_key.new_video) ? 'warning' : undefined}>{`${
                  data.self_tapes_requirements.video?.length
                }${data.self_tapes_requirements.video?.length === 1 ? 'x Video' : 'x Videos'}`}</CustomTypography>
              )}
              {data.self_tapes_requirements.files?.length > 0 && (
                <CustomTypography color={hasKey(requirements_key?.new_file) ? 'warning' : undefined}>{`${
                  data.self_tapes_requirements.files?.length
                }${
                  data.self_tapes_requirements.files?.length === 1
                    ? 'x Scene (Side Attached)'
                    : 'x Scenes (Sides Attached)'
                }`}</CustomTypography>
              )}
            </MediaList>
          </div>
        )}
        <div style={{ flex: 1 }}>
          {data && (data.project.medium_attachments?.length > 0 || (data.photos as any).length > 0) ? (
            // <div>
            //   <PlainHeading color="primary" style={{ fontWeight: 500 }}>
            //     Ref Images/Files:
            //   </PlainHeading>

            //   <>
            //     {(data.photos as any).length ? (
            //       <ImageContainer>
            //         {(data.photos as any).map((i: any, idx: number) => (
            //           <MediaItem
            //             src={i.attachment}
            //             style={{ objectFit: 'cover' }}
            //             onClick={() => handleClick(idx + 1)}
            //           />
            //         ))}
            //       </ImageContainer>
            //     ) : (
            //       <React.Fragment />
            //     )}

            //     <ScrollableContainer>
            //       {data.project.medium_attachments.filter((v) => v.medium.file_type.toLowerCase() === 'Document')?.length > 0 ? (
            //         data.project.medium_attachments
            //           .filter((v) => v.medium.file_type.toLowerCase() === 'Document')
            //           .map((i) => (
            //             <FileItem onClick={() => window.open(`${i.attachment}`, '_blank')}>
            //               <DocumentFileIcon />
            //               <Typography fontSize={12}>{i.medium.file_name}</Typography>
            //             </FileItem>
            //           ))
            //       ) : (
            //         <React.Fragment />
            //       )}
            //       {data.project.medium_attachments.filter((v) => v.medium.file_type.toLowerCase() === 'Audio')?.length > 0 ? (
            //         data.project.medium_attachments
            //           .filter((v) => v.medium.file_type.toLowerCase() === 'Audio')
            //           .map((i) => <AudioItem aud={i} src={i.attachment_url} />)
            //       ) : (
            //         // .map((i) => (
            //         //   <FileItem onClick={() => window.open(`${i.attachment}`, '_blank')}>
            //         //     <DocumentFileIcon />
            //         //     <Typography fontSize={12}>{i.medium.file_name}</Typography>
            //         //   </FileItem>
            //         // ))
            //         <React.Fragment />
            //       )}
            //       {data.project.medium_attachments.filter((v) => v.medium.file_type.toLowerCase() === 'Video')?.length > 0 ? (
            //         data.project.medium_attachments
            //           .filter((v) => v.medium.file_type.toLowerCase() === 'Video')
            //           // .map((i) => <VideoItem url={i.attachment} />)
            //           .map((i) => (
            //             <FileItem onClick={() => window.open(`${i.attachment}`, '_blank')}>
            //               <DocumentFileIcon />
            //               <Typography fontSize={12}>{i.medium.file_name}</Typography>
            //             </FileItem>
            //           ))
            //       ) : (
            //         <React.Fragment />
            //       )}
            //     </ScrollableContainer>
            //   </>
            // </div>
            <RefFiles
              audio={((data.sides as any) || []).filter((v: any) => v.medium.file_type.toLowerCase() === 'audio')}
              videos={((data.sides as any) || []).filter((v: any) => v.medium.file_type.toLowerCase() === 'video')}
              docs={((data.sides as any) || []).filter((v: any) => v.medium.file_type.toLowerCase() === 'document')}
              photos={(data.photos as any) || []}
              links={data.reference_urls || []}
            />
          ) : (
            <React.Fragment />
          )}
        </div>
      </Mediacont>

      {data?.talent_note ? (
        <div style={{ marginTop: 10 }}>
          <PlainHeading style={{ color: '#f7bc02', fontWeight: 500 }}>Casting's Message:</PlainHeading>
          <CustomTypography>{data?.talent_note}</CustomTypography>
        </div>
      ) : (
        <React.Fragment />
      )}
    </div>
  );
};

export default STLayout;
